/**
 * useWorkflowState Hook
 * 
 * Purpose: Provide React integration for RequirementsStateManager + WorkflowAdapter.
 * This hook is the ONLY way UI components should interact with workflow state and API.
 * 
 * Responsibilities:
 * - Combine state manager + adapter in React-friendly hook
 * - Handle async operations with proper loading/error states
 * - Manage side effects (API calls on form submission)
 * - Provide memoized selectors for expensive computations
 * 
 * Modularity Principle: UI components use this hook, hook uses state manager + adapter,
 * adapter calls API. No UI component should ever import backend services directly.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { RequirementsFormData, WorkflowStep } from '../services/RequirementsStateManager';
import { RequirementsStateManager } from '../services/RequirementsStateManager';
import type { RecommendationResult, ValidationResult } from '../services/WorkflowAdapter';
import { workflowAdapter } from '../services/WorkflowAdapter';

interface UseWorkflowStateReturn {
    // Current state
    currentStep: WorkflowStep;
    formData: RequirementsFormData;
    result: RecommendationResult | null;
    validation: ValidationResult | null;
    hasChanges: boolean;
    isFormComplete: boolean;
    loading: boolean;
    error: Error | null;
    sessionId: string | null;

    // Form field states
    isIndustryComplete: boolean;
    isCompanySizeComplete: boolean;
    hasBusinessProcesses: boolean;

    // Actions
    setIndustry: (industry: string) => void;
    setCompanySize: (size: string) => void;
    addBusinessProcess: (process: string) => void;
    removeBusinessProcess: (process: string) => void;
    addSpecificNeed: (need: string) => void;
    removeSpecificNeed: (need: string) => void;
    start: () => void;
    submitRequirements: () => Promise<void>;
    updateRequirements: () => Promise<void>;
    acceptRecommendations: () => Promise<void>;
    prepareInstallation: () => Promise<any>;
    executeInstallation: (installationConfig: any) => Promise<any>;
    resetForm: () => void;
    clearError: () => void;
    // Chat history utilities
    chatHistory: Array<{
        id: string;
        question: string;
        answer: string;
        timestamp?: number;
    }>;
    addChatMessage: (question: string, answer: string) => void;
    clearChatHistory: () => void;

    // Utility
    getStateSummary: () => Record<string, unknown>;
}

export function useWorkflowState(initialData?: RequirementsFormData | null): UseWorkflowStateReturn {
    // Initialize state manager (stable across renders)
    const stateManagerRef = useRef<RequirementsStateManager>(
        new RequirementsStateManager(initialData || undefined)
    );
    const stateManager = stateManagerRef.current;

    // React state for triggering re-renders
    const [, forceUpdate] = useState({});

    // Async operation states
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const [result, setResult] = useState<RecommendationResult | null>(null);
    const [validation, setValidation] = useState<ValidationResult | null>(null);
    const [sessionId, setSessionId] = useState<string | null>(null);

    // Subscribe to state manager changes
    useEffect(() => {
        const unsubscribe = stateManager.subscribe(() => {
            forceUpdate({}); // Force re-render when state changes
        });

        return unsubscribe;
    }, [stateManager]);

    // Memoized current state
    const currentState = useMemo(() => ({
        currentStep: stateManager.currentStep,
        formData: stateManager.formData,
        hasChanges: stateManager.hasChanges,
        isFormComplete: stateManager.isFormComplete,
        isIndustryComplete: stateManager.isIndustryComplete,
        isCompanySizeComplete: stateManager.isCompanySizeComplete,
        hasBusinessProcesses: stateManager.hasBusinessProcesses,
        chatHistory: stateManager.chatHistory,
    }), [
        stateManager.currentStep,
        stateManager.formData,
        stateManager.hasChanges,
        stateManager.isFormComplete,
        stateManager.isIndustryComplete,
        stateManager.isCompanySizeComplete,
        stateManager.hasBusinessProcesses,
        stateManager.chatHistory,
    ]);

    // Actions
    const setIndustry = useCallback((industry: string) => {
        stateManager.setIndustry(industry);
    }, [stateManager]);

    const setCompanySize = useCallback((size: string) => {
        stateManager.setCompanySize(size);
    }, [stateManager]);

    const addBusinessProcess = useCallback((process: string) => {
        stateManager.addBusinessProcess(process);
    }, [stateManager]);

    const removeBusinessProcess = useCallback((process: string) => {
        stateManager.removeBusinessProcess(process);
    }, [stateManager]);

    const addSpecificNeed = useCallback((need: string) => {
        stateManager.addSpecificNeed(need);
    }, [stateManager]);

    const removeSpecificNeed = useCallback((need: string) => {
        stateManager.removeSpecificNeed(need);
    }, [stateManager]);

    const start = useCallback(() => {
        stateManager.start();
    }, [stateManager]);

    const resetForm = useCallback(() => {
        stateManager.resetForm();
        setResult(null);
        setValidation(null);
        setSessionId(null);
        setError(null);
    }, [stateManager]);

    const clearError = useCallback(() => {
        setError(null);
    }, []);

    const getStateSummary = useCallback(() => {
        return stateManager.getStateSummary();
    }, [stateManager]);

    /**
     * Submit requirements to get recommendations
     */
    const submitRequirements = useCallback(async () => {
        if (!stateManager.isFormComplete) {
            setError(new Error('Form is incomplete'));
            return;
        }

        setLoading(true);
        setError(null);

        try {
            console.log('useWorkflowState: Submitting requirements');

            const formData = stateManager.formData;
            const recommendationResult = await workflowAdapter.submitRequirements(formData);

            setResult(recommendationResult);
            stateManager.showResults();

            // Generate a simple session ID for now (in production, this would come from backend)
            setSessionId(`session-${Date.now()}`);

            console.log('useWorkflowState: Recommendations received', recommendationResult);
        } catch (err) {
            console.error('useWorkflowState: Error submitting requirements', err);
            setError(err instanceof Error ? err : new Error('Unknown error occurred'));
        } finally {
            setLoading(false);
        }
    }, [stateManager]);

    /**
     * Update requirements (when user edits in results view)
     */
    const updateRequirements = useCallback(async () => {
        if (!stateManager.isFormComplete) {
            setError(new Error('Form is incomplete'));
            return;
        }

        if (!stateManager.hasChanges) {
            console.log('useWorkflowState: No changes to update');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            console.log('useWorkflowState: Updating requirements');

            const formData = stateManager.formData;
            const recommendationResult = await workflowAdapter.updateRequirements(sessionId, formData);

            setResult(recommendationResult);
            stateManager.markChangesSaved();

            console.log('useWorkflowState: Updated recommendations received', recommendationResult);
        } catch (err) {
            console.error('useWorkflowState: Error updating requirements', err);
            setError(err instanceof Error ? err : new Error('Unknown error occurred'));
        } finally {
            setLoading(false);
        }
    }, [stateManager, sessionId]);

    /**
     * Accept recommendations and move to validation
     */
    const acceptRecommendations = useCallback(async () => {
        if (!result || !sessionId) {
            setError(new Error('No recommendations to accept'));
            return;
        }

        setLoading(true);
        setError(null);

        try {
            console.log('useWorkflowState: Accepting recommendations');

            const selectedModules = result.recommendedModules.map(m => m.technicalName);
            const validationResult = await workflowAdapter.validateModules(sessionId, selectedModules);

            setValidation(validationResult);
            stateManager.moveToStep('validation');

            console.log('useWorkflowState: Validation complete', validationResult);
        } catch (err) {
            console.error('useWorkflowState: Error validating recommendations', err);
            setError(err instanceof Error ? err : new Error('Unknown error occurred'));
        } finally {
            setLoading(false);
        }
    }, [result, sessionId, stateManager]);

    /**
     * Prepare installation configuration (Discrete Function)
     * This generates the build configuration without executing installation
     */
    const prepareInstallation = useCallback(async () => {
        if (!result || !sessionId) {
            setError(new Error('No recommendations to prepare'));
            return null;
        }

        setLoading(true);
        setError(null);

        try {
            console.log('useWorkflowState: Preparing installation configuration');

            const selectedModules = result.recommendedModules.map(m => m.technicalName);
            const formData = stateManager.formData;

            const installationConfig = await workflowAdapter.prepareInstallation(
                sessionId,
                selectedModules,
                formData
            );

            console.log('useWorkflowState: Installation configuration prepared', installationConfig);

            return installationConfig;
        } catch (err) {
            console.error('useWorkflowState: Error preparing installation', err);
            setError(err instanceof Error ? err : new Error('Failed to prepare installation'));
            return null;
        } finally {
            setLoading(false);
        }
    }, [result, sessionId, stateManager]);

    /**
     * Execute installation workflow (Deterministic Interface)
     * This is the discrete function that triggers the installation process
     */
    const executeInstallation = useCallback(async (installationConfig: any) => {
        if (!installationConfig) {
            setError(new Error('No installation configuration provided'));
            return null;
        }

        setLoading(true);
        setError(null);

        try {
            console.log('useWorkflowState: Executing installation');

            const installationResult = await workflowAdapter.executeInstallation(installationConfig);

            stateManager.moveToStep('orchestration');

            console.log('useWorkflowState: Installation execution started', installationResult);

            return installationResult;
        } catch (err) {
            console.error('useWorkflowState: Error executing installation', err);
            setError(err instanceof Error ? err : new Error('Failed to execute installation'));
            return null;
        } finally {
            setLoading(false);
        }
    }, [stateManager]);

    return {
        // State
        ...currentState,
        result,
        validation,
        loading,
        error,
        sessionId,

        // Actions
        setIndustry,
        setCompanySize,
        addBusinessProcess,
        removeBusinessProcess,
        addSpecificNeed,
        removeSpecificNeed,
        start,
        submitRequirements,
        updateRequirements,
        acceptRecommendations,
        prepareInstallation,
        executeInstallation,
        resetForm,
        clearError,
        // Chat
        addChatMessage: (question: string, answer: string) => stateManager.addChatMessage(question, answer),
        clearChatHistory: () => stateManager.clearChatHistory(),
        chatHistory: stateManager.chatHistory,
        getStateSummary,
    };
}
